import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import {
  getSupportedDevicesSync,
  isBeiyingDevice8815X98Keyboard,
  isHfdAdvancedKeyboardAllowed,
  isHuafendaMagneticAxisEquipment,
  resolveCatalogRowFromSnapshot,
} from "@eweadn-qiankun/shared-utils";
import type { MenuItem } from "../Menu/types";
import {
  keyboardMenuData,
  keyboardMenuDataBeiying,
  keyboardMenuDataHuafenda,
  mouseMenuDataBeiying,
  mouseMenuDataFreq,
  mouseMenuDataJiangMeng,
  mouseMenuDataZhanli,
} from "../Menu/menuData";

export type ConfigPreviewMenuId =
  | "lighting"
  | "trigger"
  | "changeKey"
  | "advancedKey"
  | "performance"
  | "dpi"
  | "parameter"
  | "dynamicSensitivity";

export interface ConfigPreviewMenuItem {
  id: ConfigPreviewMenuId;
  labelKey: string;
  icon: string;
}

/** 子应用 path → 社区预览 section（仅含已有预览面板的项） */
const SUBAPP_PATH_TO_PREVIEW_ID: Record<string, ConfigPreviewMenuId> = {
  lighting: "lighting",
  trigger: "trigger",
  changeKey: "changeKey",
  advancedKey: "advancedKey",
  performance: "performance",
  dpiSetting: "dpi",
  triggerSetting: "trigger",
  parameter: "parameter",
  dynamicSensitivity: "dynamicSensitivity",
};

const PREVIEWABLE_PATHS = new Set(Object.keys(SUBAPP_PATH_TO_PREVIEW_ID));

/** 与 mouse-management `mouseSupportsMagneticMicroswitch` / supportedDevices 一致 */
function mouseSupportsMagneticMicroswitch(deviceId: string): boolean {
  if (!deviceId) return false;
  return (
    getSupportedDevicesSync().mouse as Array<{
      deviceId?: string;
      magneticMicroswitch?: boolean;
    }>
  ).some(
    (row) => row.deviceId === deviceId && row.magneticMicroswitch === true
  );
}

/** 与 Menu.tsx 高级键白名单一致：走 shared-utils 判定（含经典 X87 SOCD） */
/** 与 Menu.tsx 华奋达高级键规则一致（含磁轴 battle68） */
interface PreviewMenuFilterCtx {
  solutionProvider: string;
  keyboardType: string;
  displayname: string;
  productName: string;
  identifier: string;
  mouseDeviceId: string;
  vendorId?: number;
  productId?: number;
  isX98_1K: boolean;
  hfdId: string;
  magneticAxis?: boolean;
}

function resolveDeviceRecord(
  meta: CommunityPreviewDeviceMeta,
  category: "keyboard" | "mouse"
): Record<string, unknown> | undefined {
  const fromCatalog = resolveCatalogRowFromSnapshot(
    meta.deviceCatalog,
    category
  );
  if (fromCatalog) return fromCatalog;

  const list = getSupportedDevicesSync()[category] as Array<
    Record<string, unknown>
  >;
  return list.find(
    (row) =>
      String(row.displayname ?? "") === meta.displayname &&
      String(row.keyboardType ?? "") === meta.keyboardType &&
      String(row.solutionRovider ?? "").toLowerCase() ===
        meta.solutionProvider.toLowerCase()
  );
}

function resolvePreviewMenuFilterCtx(
  meta: CommunityPreviewDeviceMeta,
  deviceCategory: "keyboard" | "mouse"
): PreviewMenuFilterCtx {
  const device = resolveDeviceRecord(meta, deviceCategory);

  const vendorId = Number(device?.vendorId);
  const productId = Number(device?.productId);
  const displayname = String(device?.displayname ?? meta.displayname);
  const productName = String(device?.productName ?? "");
  const keyboardType = String(device?.keyboardType ?? meta.keyboardType);
  const identifier = String(device?.identifier ?? "");

  const equipmentLike = {
    vendorId: Number.isFinite(vendorId) ? vendorId : undefined,
    productId: Number.isFinite(productId) ? productId : undefined,
    keyboardType,
    displayname,
  };

  return {
    solutionProvider: meta.solutionProvider,
    keyboardType,
    displayname,
    productName,
    mouseDeviceId: String(device?.deviceId ?? meta.mouseDeviceId ?? ""),
    vendorId: Number.isFinite(vendorId) ? vendorId : undefined,
    productId: Number.isFinite(productId) ? productId : undefined,
    isX98_1K: isBeiyingDevice8815X98Keyboard(equipmentLike),
    hfdId: String(
      device?.identifier ?? `${vendorId}:${productId}:${displayname}`
    ),
    identifier,
    magneticAxis: device?.magneticAxis === true,
  };
}

/** 对齐 Menu.tsx shouldShowMenuItem（仅预览相关 path） */
function shouldShowPreviewMenuItem(
  path: string,
  ctx: PreviewMenuFilterCtx
): boolean {
  const { solutionProvider: solutionRovider, keyboardType } = ctx;

  if (solutionRovider === "beiying" || solutionRovider === "beiyingMachine") {
    const is916Machine = ["m80", "m71", "t87", "x75v2", "m75"].includes(
      keyboardType
    );
    // 916 / 8815 不展示高级键
    if ((is916Machine || ctx.isX98_1K) && path === "advancedKey") {
      return false;
    }
    // 8815(X98_1K) 不展示性能；916 开放性能（含无线休眠）
    if (ctx.isX98_1K && path === "performance") {
      return false;
    }
  }

  if (solutionRovider === "rongyuan") {
    if (path === "trigger" || path === "testCalibration") {
      return false;
    }
  }

  if (solutionRovider === "huafenda") {
    const isMagnetic = isHuafendaMagneticAxisEquipment({
      solutionRovider: "huafenda",
      identifier: ctx.identifier,
      displayname: ctx.displayname,
      keyboardType: ctx.keyboardType,
      magneticAxis: ctx.magneticAxis,
    });
    if ((path === "trigger" || path === "testCalibration") && !isMagnetic) {
      return false;
    }
    if (
      path === "advancedKey" &&
      !isHfdAdvancedKeyboardAllowed(ctx.hfdId, ctx.identifier ?? "", {
        displayname: ctx.displayname,
        keyboardType: ctx.keyboardType,
        magneticAxis: ctx.magneticAxis,
        productName: ctx.productName,
      })
    ) {
      return false;
    }
  }

  if (solutionRovider === "freq") {
    const isFreqLightingSupported = ["a6", "a6pro", "a6max", "g2sea"].includes(
      ctx.mouseDeviceId.toLowerCase()
    );
    if (path === "lighting" && !isFreqLightingSupported) return false;
  }

  // 与子应用 mouse-management：非磁微动型号隐藏「触发设置」；展立无触发页
  if (path === "triggerSetting") {
    if (solutionRovider === "zhanli") return false;
    if (
      solutionRovider === "jiangmeng" &&
      !mouseSupportsMagneticMicroswitch(ctx.mouseDeviceId)
    ) {
      return false;
    }
  }

  return true;
}

function getKeyboardMenuDataByProvider(provider: string): MenuItem[] {
  if (provider === "huafenda") return keyboardMenuDataHuafenda;
  if (provider === "beiying" || provider === "beiyingMachine") {
    return keyboardMenuDataBeiying;
  }
  return keyboardMenuData;
}

function getMouseMenuDataByProvider(provider: string): MenuItem[] {
  if (provider === "beiying") return mouseMenuDataBeiying;
  if (provider === "freq") return mouseMenuDataFreq;
  if (provider === "zhanli") return mouseMenuDataZhanli;
  // yxt / jiangmeng 及其它：匠盟菜单结构
  return mouseMenuDataJiangMeng;
}

function extractPreviewMenusFromSubAppMenu(
  menuData: MenuItem[],
  ctx: PreviewMenuFilterCtx
): ConfigPreviewMenuItem[] {
  const items: ConfigPreviewMenuItem[] = [];

  for (const group of menuData) {
    for (const child of group.children ?? []) {
      const path = child.path;
      if (!path || !PREVIEWABLE_PATHS.has(path)) continue;
      if (!shouldShowPreviewMenuItem(path, ctx)) continue;

      const id = SUBAPP_PATH_TO_PREVIEW_ID[path];
      if (items.some((item) => item.id === id)) continue;

      items.push({
        id,
        labelKey: child.title ?? `community.preview.menu.${id}`,
        icon: child.icon ?? id,
      });
    }
  }

  return items;
}

/** 星闪默认（无 meta 时兜底） */
export const KEYBOARD_PREVIEW_MENUS: ConfigPreviewMenuItem[] = [
  {
    id: "lighting",
    labelKey: "keyboard.menu.lighting",
    icon: "light",
  },
  {
    id: "trigger",
    labelKey: "keyboard.menu.trigger",
    icon: "trigger",
  },
  {
    id: "changeKey",
    labelKey: "keyboard.menu.changeKey",
    icon: "changekey",
  },
  {
    id: "advancedKey",
    labelKey: "keyboard.menu.advancedKey",
    icon: "advancekey",
  },
  {
    id: "performance",
    labelKey: "keyboard.menu.performance",
    icon: "parameter",
  },
];

/** 匠盟默认（无 meta 时兜底） */
export const MOUSE_PREVIEW_MENUS: ConfigPreviewMenuItem[] = [
  {
    id: "changeKey",
    labelKey: "mouse.menu.changeKey",
    icon: "changekey",
  },
  { id: "dpi", labelKey: "mouse.menu.dpiSetting", icon: "dpi" },
  {
    id: "trigger",
    labelKey: "mouse.menu.triggerSetting",
    icon: "trigger",
  },
  {
    id: "performance",
    labelKey: "mouse.menu.performance",
    icon: "performance",
  },
  {
    id: "parameter",
    labelKey: "mouse.menu.parameter",
    icon: "parameter",
  },
  {
    id: "lighting",
    labelKey: "mouse.menu.lighting",
    icon: "light",
  },
];

export function getPreviewMenus(
  deviceCategory: "keyboard" | "mouse",
  meta?: CommunityPreviewDeviceMeta
): ConfigPreviewMenuItem[] {
  const fallback =
    deviceCategory === "keyboard"
      ? KEYBOARD_PREVIEW_MENUS
      : MOUSE_PREVIEW_MENUS;

  if (!meta) return fallback;

  const ctx = resolvePreviewMenuFilterCtx(meta, deviceCategory);
  const menuData =
    deviceCategory === "keyboard"
      ? getKeyboardMenuDataByProvider(ctx.solutionProvider)
      : getMouseMenuDataByProvider(ctx.solutionProvider);

  const items = extractPreviewMenusFromSubAppMenu(menuData, ctx);
  return items.length > 0 ? items : fallback;
}
